import { NgClass } from '@angular/common';
import { Component, HostListener, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faPowerOff } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Subject, Subscription, catchError, from, takeUntil } from 'rxjs';
import { ApplicationMenuComponent } from './components/menu/application-menu/application-menu.component';
import { BannerMenuComponent } from './components/menu/banner-menu/banner-menu.component';
import { MenuSettingsComponent } from './components/menu/banner-settings-menu/menu-settings.component';
import { UserProfileComponent } from './components/profile/user-profile.component';
import { OrganizationSelectorComponent } from './controls/organization-selector/organization-selector.component';
import { ViewSelectorComponent } from './controls/view-selector/view-selector.component';
import { HttpError } from './core/http-error';
import { Authenticate } from './models/auth/authenticate';
import { Rights } from './models/enums/rights';
import { Organization } from './models/organization';
import { View } from './models/view';
import { AuthService } from './services/auth.service';
import { DialogService } from './services/dialog.service';
import { MemoryService } from './services/memory.service';
import { NotificationService } from './services/notification-service';
import { OrganizationsService } from './services/organizations.service';
import { SessionTimeoutService } from './services/session-timeout.service';
import { ThemesService } from './services/themes.service';
import { ViewsService } from './services/views.service';
import { WebSocketNotificationService } from './services/ws/websocket-notification.service';
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        MenuSettingsComponent,
        OrganizationSelectorComponent,
        ViewSelectorComponent,
        UserProfileComponent,
        BannerMenuComponent,
        MatTooltipModule,
        RouterLink,
        FaIconComponent,
        ApplicationMenuComponent,
        NgClass,
        RouterOutlet,
        NgxSpinnerModule,
        MatIconModule,
    ],
})
export class AppComponent implements OnDestroy {
    title = 'Hub4B BackOffice';

    public powerOffIcon = faPowerOff;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * Utilisateur connecté
     */
    public userConnected: Authenticate = null;

    /**
     * Delai (en secondes) avant l'expiration de la session pour afficher la notification à l'utilisateur.
     */
    public sessionNotificationDelay = 60;

    public selectedOrganization: Organization;

    public organizations: Organization[] = null;

    public organizationSelectionVisible: boolean = false;

    public views: View[] = null;

    public selectedView: View;

    organisationsAllowedToAccessHub4BChange: Subscription;

    organisationViews: Subscription;

    public isDarkMode: boolean;

    constructor(
        private router: Router,
        protected notificationService: NotificationService,
        protected memoryService: MemoryService,
        protected authService: AuthService,
        private organizationService: OrganizationsService,
        public dialog: MatDialog,
        private timeoutService: SessionTimeoutService,
        private viewsService: ViewsService,
        protected dialogService: DialogService,
        protected webSocketNotificationService: WebSocketNotificationService,
        private themesService: ThemesService
    ) {
        this.themesService.changeTheme(this.themesService.getCurrentTheme(), true);

        this.userConnected = this.memoryService.getConnectedUser();

        // Si l'utilisateur est connecté
        if (!!this.userConnected) {
            // Rechargement
            this.reload();
        }

        // this.removePreviousTheme();

        // /* Specifie le theme par défaut */
        // document.body.classList.add('avm-dark-theme');

        this.memoryService.connectedUserChangeEvent.subscribe((x) => {
            // Si l'utilisateur connecté est toujours le même --> on ne fait rien
            if (x && this.userConnected && x.userId === this.userConnected.userId) {
                return;
            }

            this.userConnected = x;
            if (!!this.userConnected) {
                this.webSocketNotificationService.startSocket(x.organization.id, x.login);

                // if (!!this.userConnected.uiTheme) {
                //     this.removePreviousTheme();
                //     document.body.classList.add(`avm-${this.userConnected.uiTheme}-theme`);
                // }

                if (!this.memoryService.getConnectedUser().hasRight(Rights.ChildOrganizationAdministration)) {
                    this.organizationSelectionVisible = false;

                    // L'utilisateur connecté n'a pas le droit de selectionner une organisation --> selection de l'organisation de l'utilisateur.
                    this.organizationService.get(this.memoryService.getConnectedUser().organization.id).subscribe((organization) => {
                        this.memoryService.selectOrganization(organization);
                        this.router.navigate(['/home']);
                    });
                } else {
                    this.organizationService.getOrganisationsAllowedToAccessHub4B(this.memoryService.getConnectedUser().organization).subscribe({
                        next: (organisationsAllowedToAccessHub4B) => {
                            this.memoryService.selectOrganization(this.memoryService.getConnectedUser().organization);

                            // Si l'utilisateur a accès à des d'organisations enfants --> Affichage du composant de selection des organisations.
                            if (organisationsAllowedToAccessHub4B.length !== 1) {
                                this.organizationSelectionVisible = true;
                            }

                            this.router.navigate(['/home']);
                        },
                        error: (error: HttpError) => {
                            this.notificationService.displayError('Erreur', error.message);
                        },
                    });
                }
            }
        });

        // Abonnement au changement de la liste des organisations autorisées
        this.organisationsAllowedToAccessHub4BChange = this.organizationService.organisationsAllowedToAccessHub4BChange$.subscribe((x) => {
            this.organizations = x;
        });

        // Abonnement au changement de la liste des vues
        this.organisationViews = this.viewsService.organisationViews$.subscribe((x) => {
            this.views = x;

            // Si la vue sélectionnée n'est plus présente dans la liste des vues
            if (!x?.find((a) => a.id === this.selectedView?.id)) {
                this.memoryService.selectView(null);
            }
            // Sinon si le nom de la vue a changé
            else if (this.selectedView?.name !== x?.find((a) => a.id === this.memoryService.getSelectedView().id).name) {
                // Réaffecte la vue sélectionnée
                this.selectedView = x.find((a) => a.id === this.memoryService.getSelectedView().id);
            }
        });

        this.memoryService.organizationChangeEvent.subscribe((x) => {
            this.selectedOrganization = x;

            if (this.selectedOrganization) {
                this.viewsService.listAll().subscribe((v) => {
                    this.views = v;
                    this.memoryService.selectView(null);
                });
            } else {
                this.views = null;
                this.memoryService.selectView(null);
            }
        });

        this.memoryService.selectedViewChange$.subscribe((x) => {
            this.selectedView = x;
        });

        this.subscribeToSessionTimeout();
    }

    private reload() {
        if (!this.memoryService.getConnectedUser().hasRight(Rights.ChildOrganizationAdministration)) {
            this.organizationSelectionVisible = false;

            // L'utilisateur connecté n'a pas le droit de selectionner une organisation --> selection de l'organisation de l'utilisateur.
            this.organizationService.get(this.memoryService.getConnectedUser().organization.id).subscribe((organization) => {
                this.memoryService.selectOrganization(organization);
                this.router.navigate(['/home']);
            });
        } else {
            this.organizationService.getOrganisationsAllowedToAccessHub4B(this.memoryService.getConnectedUser().organization).subscribe({
                next: (organisationsAllowedToAccessHub4B) => {
                    // Si l'utilisateur a accès à des d'organisations enfants --> Affichage du composant de selection des organisations.
                    if (organisationsAllowedToAccessHub4B.length !== 1) {
                        this.organizationSelectionVisible = true;
                        this.memoryService.selectOrganization(this.memoryService.getSelectedOrganization());
                        this.selectedOrganization = this.memoryService.getSelectedOrganization();
                    }
                },
                error: (error: HttpError) => {
                    this.notificationService.displayError('Erreur', error.message);
                },
            });
        }
    }

    ngOnInit() {
        this.themesService.currentTheme$.pipe(takeUntil(this.componentDestroyed$)).subscribe((theme) => {
            this.isDarkMode = theme.includes('dark') ? true : false;
        });
    }

    /**
     * Détruit le composant.
     */
    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
        this.organisationsAllowedToAccessHub4BChange.unsubscribe();
    }

    /**
     * Gestion de la deconnexion
     */
    public logoff(): void {
        this.authService
            .logoff(this.userConnected.token.accessToken)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((X) => {
                this.router.navigate(['/login']);
            });
    }

    public onOrganizationSelected(organization: Organization) {
        console.log('onOrganizationSelected : ', organization);
        this.memoryService.selectOrganization(organization);
        this.router.navigate(['/home']);
    }

    public onViewSelected(view: View) {
        console.log('onViewSelected : ', view);
        this.memoryService.selectView(view);
        this.router.navigate(['/home']);
    }

    public onThemeChange() {
        this.themesService.changeTheme(this.isDarkMode ? 'avm-light-theme' : 'avm-dark-theme', true);
    }

    /**
     * Retire du DOM toute classe relative à un theme.
     */
    // private removePreviousTheme() {
    //     /* Retire un eventuel theme présent d'une precedente connexion */
    //     const classList: string[] = Array.from(document.body.classList);

    //     const themeClassList = classList.filter((x) => x.toLowerCase().endsWith('-theme'));

    //     themeClassList.forEach((x) => document.body.classList.remove(x));
    // }

    private subscribeToSessionTimeout() {
        this.timeoutService.notificationDelay = this.sessionNotificationDelay;
        this.timeoutService.timeoutExpired.pipe(takeUntil(this.componentDestroyed$)).subscribe((res) => {
            //const modalPromise = this.dialogService.openSessionTimeout(this.memoryService.getConnectedUser().token.expireOn, this.sessionNotificationDelay);

            console.log('Vérifie auprès du serveur si le token a été prolongé...');
            this.authService.checkToken().subscribe(
                (x) => {
                    if (this.timeoutService.calculateRemainingSessionTime(x.expireOn) <= this.timeoutService.notificationDelay * 1000) {
                        console.log("Le token n'a pas été prolongé");
                        console.log("La session est sur le point d'expirer.");
                        this.notifyUserAboutSessionTimeout();
                    } else {
                        console.log('La session a été prolongée');
                        // La session a du être prolongée par une action utilisateur --> on reinitialise le timer avec la nouvelle date d'expiration.
                        this.timeoutService.startTimer(x.expireOn);
                    }
                },
                catchError((error) => {
                    console.log(`User in not connected : Token is expired`, error);
                    this.memoryService.setConnectedUser(null);
                    this.memoryService.selectOrganization(null);
                    this.router.navigate(['/login']);
                    throw error;
                })
            );
        });
    }

    private notifyUserAboutSessionTimeout() {
        const modalPromise = this.dialogService.openSessionTimeout(this.memoryService.getConnectedUser().token.expireOn, this.sessionNotificationDelay);
        const newObservable = from(modalPromise);
        newObservable.subscribe(
            (resp) => {
                if (resp === true) {
                    console.log('Extending session...');
                    this.authService.keepAliveToken().subscribe((x) => {
                        this.timeoutService.startTimer(this.memoryService.getConnectedUser().token.expireOn);
                    });
                } else {
                    console.log('Not extending session...');
                    this.dialogService.closeAll();
                    this.logoff();
                }
            },
            (reason) => {
                console.log('Dismissed ' + reason);
                this.dialogService.closeAll();
                this.logoff();
            }
        );
    }

    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event) {
        if (this.userConnected) {
            this.authService.wildLogoff(this.userConnected.token.accessToken);
        }
    }
}
